import {
  CalendarDays,
  Check,
  ChevronRight,
  CircleUserRound,
  CreditCard,
  Eye,
  UserRoundPlus,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const workflowSteps = [
  {
    label: 'Booking request',
    detail: 'Received 09:14',
    icon: CalendarDays,
    complete: true,
  },
  {
    label: 'Approve',
    detail: 'Slot confirmed',
    icon: Check,
    complete: true,
  },
  {
    label: 'Assign cleaner',
    detail: 'Maya assigned',
    icon: UserRoundPlus,
    complete: true,
  },
  {
    label: 'Payment requested',
    detail: 'Invoice · £145',
    icon: CreditCard,
    complete: false,
  },
];

export function BookingWorkflowMockup({ compact = false, decorative = false }: { compact?: boolean; decorative?: boolean }) {
  return (
    <div
      data-testid={decorative ? undefined : "marketing-booking-workflow-mockup"}
      className="overflow-hidden rounded-2xl border border-border bg-card shadow-xl"
    >
      <div className="flex items-center justify-between border-b border-border bg-muted/35 px-4 py-3 sm:px-5">
        <div>
          <p className="text-sm font-semibold text-foreground">Booking requests</p>
          <p className="text-[11px] text-muted-foreground">Review before adding to the schedule</p>
        </div>
        <span className="rounded-md bg-amber-100 px-2 py-1 text-[11px] font-semibold text-amber-800">
          1 pending
        </span>
      </div>

      <div className={cn('p-4 sm:p-5', !compact && 'space-y-5')}>
        <div className="rounded-xl border border-border bg-background p-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex min-w-0 gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <CircleUserRound className="h-4 w-4" />
              </span>
              <div className="min-w-0">
                <p className="font-semibold text-foreground">Deep clean</p>
                <p className="text-xs text-muted-foreground">Aisha Morgan · Cardiff</p>
                <p className="mt-2 text-xs font-medium text-foreground">Tue 22 Jul · 10:00–13:00</p>
              </div>
            </div>
            <div className="shrink-0 text-left sm:text-right">
              <p className="text-sm font-semibold text-foreground">£145.00</p>
              <p className="text-[11px] text-muted-foreground">Payment after approval</p>
            </div>
          </div>
          <div className="mt-4 flex gap-2 border-t border-border pt-3">
            <span className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground">
              <Eye className="h-3.5 w-3.5" /> View
            </span>
            <span className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground">
              <Check className="h-3.5 w-3.5" /> Approve
            </span>
          </div>
        </div>

        <div className={cn('grid gap-2', compact ? 'mt-4 sm:grid-cols-2' : 'sm:grid-cols-4')}>
          {workflowSteps.map((step, index) => (
            <div key={step.label} className="relative flex items-center gap-2 rounded-lg border border-border bg-background px-3 py-2.5">
              <span
                className={cn(
                  'flex h-7 w-7 shrink-0 items-center justify-center rounded-md',
                  step.complete ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground',
                )}
              >
                <step.icon className="h-3.5 w-3.5" />
              </span>
              <div className="min-w-0">
                <p className="truncate text-[11px] font-semibold text-foreground">{step.label}</p>
                <p className="truncate text-[10px] text-muted-foreground">{step.detail}</p>
              </div>
              {index < workflowSteps.length - 1 && !compact && (
                <ChevronRight className="absolute -right-3.5 top-1/2 z-10 hidden h-3 w-3 -translate-y-1/2 text-muted-foreground sm:block" />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const scheduleColumns = [
  {
    name: 'Unassigned',
    muted: true,
    jobs: [{ time: '09:30', title: 'Regular clean', customer: 'N. Clarke', tone: 'amber' }],
  },
  {
    name: 'Maya',
    jobs: [
      { time: '10:00', title: 'Deep clean', customer: 'A. Morgan', tone: 'purple' },
      { time: '14:30', title: 'Office clean', customer: 'Cedar House', tone: 'blue' },
    ],
  },
  {
    name: 'Lewis',
    jobs: [{ time: '08:30', title: 'End of tenancy', customer: 'R. Evans', tone: 'green' }],
  },
] as const;

const toneClasses = {
  amber: 'border-amber-200 bg-amber-50 text-amber-950',
  purple: 'border-primary/25 bg-primary/10 text-foreground',
  blue: 'border-sky-200 bg-sky-50 text-sky-950',
  green: 'border-emerald-200 bg-emerald-50 text-emerald-950',
};

export function TeamScheduleMockup({ decorative = false }: { decorative?: boolean } = {}) {
  return (
    <div
      data-testid={decorative ? undefined : "marketing-team-schedule-mockup"}
      className="overflow-hidden rounded-2xl border border-border bg-card shadow-xl"
    >
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border bg-muted/35 px-4 py-3 sm:px-5">
        <div>
          <p className="text-sm font-semibold text-foreground">Schedule</p>
          <p className="text-[11px] text-muted-foreground">Tue 22 July</p>
        </div>
        <div className="flex rounded-lg border border-border bg-background p-1 text-[11px] font-medium">
          <span className="rounded-md bg-primary px-2.5 py-1 text-primary-foreground">Team</span>
          <span className="px-2.5 py-1 text-muted-foreground">Week</span>
        </div>
      </div>

      <div className="grid min-w-[560px] grid-cols-3 divide-x divide-border">
        {scheduleColumns.map((column) => (
          <div key={column.name} className={cn('min-h-64 p-3', column.muted && 'bg-muted/20')}>
            <div className="mb-3 flex items-center justify-between">
              <p className="text-xs font-semibold text-foreground">{column.name}</p>
              <span className="text-[10px] text-muted-foreground">{column.jobs.length}</span>
            </div>
            <div className="space-y-2">
              {column.jobs.map((job) => (
                <div key={`${job.time}-${job.title}`} className={cn('rounded-lg border p-2.5', toneClasses[job.tone])}>
                  <p className="text-[10px] font-semibold">{job.time}</p>
                  <p className="mt-1 text-xs font-semibold">{job.title}</p>
                  <p className="mt-0.5 text-[10px] opacity-70">{job.customer}</p>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
